<script setup>
import { ref, onUnmounted, onMounted, computed  } from 'vue';
import { __ } from 'JIX/gettext.js';
import { setDropdownMaxHeightOnOpen } from './composables.js';
import JobsearchInputField from './JobsearchInputField.vue';

const props = defineProps({
    dropdownClasses: {
        type: [String, Object, Array],
        required: false,
        default: () => [],
    },
    numSelectableItems: {
        type: Number,
        default: 0,
    },
    dropdownHasContent: {
        type: Boolean,
        default: false,
    },
    inputClasses: {
        type: String,
        default: '',
    },
    inputIcon: {
        type: Object,
        default: () => ({}),
    },
    ariaLabel: {
        type: String,
        default: '',
    },
    placeholder: {
        type: String,
        default: '',
    },
});

const showDropdown = defineModel('showDropdown', {
    type: Boolean,
    required: true,
});

const userInput = defineModel('userInput', {
    type: String,
    default: '',
});

// Template refs
const container = ref();
const jobsearchDropdown = ref();
const inputContainer = ref();
const inputField = ref();

const emit = defineEmits(['clickOutside', 'clickInside', 'focus', 'enter', 'focusout']);

function handleClick(event) {
    if (container.value) {
        if (!container.value.contains(event.target)) {
            emit('clickOutside');
            showDropdown.value = false;
        } else {
            if (!event.target.matches("input")) {
                inputField.value?.focusInput();
            }
            emit('clickInside');
        }
    }
}

function focusout(event) {
    if (event.relatedTarget && !container.value.contains(event.relatedTarget)) {
        showDropdown.value = false;
    }
    emit('focusout');
}

function removeHandleClick() {
    document.removeEventListener('click', handleClick, { capture: true });
}

function addHandleClick() {
    document.addEventListener('click', handleClick, { capture: true });
}

setDropdownMaxHeightOnOpen(inputContainer, jobsearchDropdown, showDropdown);

onMounted(() => {
    addHandleClick();
});
onUnmounted(removeHandleClick);


const selectedIndex = ref(null);
const clampedSelectedIndex = computed(() => {
    if (selectedIndex.value === null || props.numSelectableItems === 0) {
        return null;
    }
    return Math.min(selectedIndex.value, props.numSelectableItems - 1);
});

function moveUp() {
    if (clampedSelectedIndex.value === null) {
        selectedIndex.value = props.numSelectableItems - 1;
    } else if (clampedSelectedIndex.value === 0) {
        selectedIndex.value = null;
    } else {
        selectedIndex.value--;
    }
}

function moveDown() {
    if (clampedSelectedIndex.value === null) {
        selectedIndex.value = 0;
    } else if (clampedSelectedIndex.value === props.numSelectableItems - 1) {
        selectedIndex.value = null;
    } else {
        selectedIndex.value++;
    }
}

defineExpose({
    selectedIndex: clampedSelectedIndex,
    resetSelectedIndex: () => selectedIndex.value = null,
    inputField,
});

</script>

<template>
    <div
        @focusout="focusout"
        ref="container"
        @keydown.esc="showDropdown = false"
    >
        <div ref="inputContainer">
            <slot name="input">
                <JobsearchInputField
                    ref="inputField"
                    :aria-label="ariaLabel"
                    :placeholder="placeholder"
                    @focus="showDropdown = true"
                    @click="showDropdown = true"
                    v-model="userInput"
                    :input-classes="inputClasses"
                    @keydown="showDropdown = true"
                    @keydown.enter.prevent="emit('enter', $event, clampedSelectedIndex)"
                    @keydown.up.prevent="moveUp"
                    @keydown.down.prevent="moveDown"
                    :icon="{ size: inputIcon.size, name: inputIcon.name }"
                />
            </slot>
        </div>
        <Transition name="slide-down">
            <div class="jobsearch-dropdown-anchorpoint" tabindex="-1" v-show="showDropdown && dropdownHasContent">
                <div ref="jobsearchDropdown" class="jobsearch-dropdown" :class="dropdownClasses">
                    <slot name="dropdown" :selected-index="clampedSelectedIndex" />
                </div>
            </div>
        </Transition>
    </div>
</template>
